/**
 * SettingsPanel - Google Meet-style video settings dialog
 * Two-column layout: device controls (left) + effects and utilities (right)
 */

import { useState, useEffect } from "react";
import { Room, Track } from "livekit-client";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Label,
  Button,
  Switch,
  BackgroundPicker,
  saveDevicePreference,
  getPreferredDeviceId,
  type UseBackgroundProcessorReturn,
  type BackgroundPreset,
} from "@jourfx/ui";
import { Settings, Camera, Mic, ImageIcon, MonitorCheck, FlipHorizontal2 } from "lucide-react";
import { TechCheckModal } from "./TechCheckModal";

interface SettingsPanelProps {
  room: Room | null;
  background: UseBackgroundProcessorReturn;
  presets: BackgroundPreset[];
  isMirrored: boolean;
  onMirrorChange: (mirrored: boolean) => void;
  onClose: () => void;
}

interface DeviceInfo {
  deviceId: string;
  label: string;
}

export function SettingsPanel({
  room,
  background,
  presets,
  isMirrored,
  onMirrorChange,
  onClose,
}: SettingsPanelProps) {
  const { t } = useTranslation("video");
  const [cameras, setCameras] = useState<DeviceInfo[]>([]);
  const [microphones, setMicrophones] = useState<DeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>("");
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>("");
  const [showTechCheck, setShowTechCheck] = useState(false);

  // Enumerate devices and initialize selection from room's active tracks
  useEffect(() => {
    async function getDevices() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();

        // Strip device IDs from labels (e.g. "FaceTime Camera (0x1420...)" → "FaceTime Camera")
        const cleanLabel = (label: string) => label.replace(/\s*\([0-9a-fx:]+\)\s*$/i, "").trim();

        const cameraDevices = devices
          .filter((d) => d.kind === "videoinput")
          .map((d, i) => ({ deviceId: d.deviceId, label: cleanLabel(d.label) || `${t("settings.camera")} ${i + 1}` }));

        const micDevices = devices
          .filter((d) => d.kind === "audioinput")
          .map((d, i) => ({ deviceId: d.deviceId, label: cleanLabel(d.label) || `${t("settings.microphone")} ${i + 1}` }));

        setCameras(cameraDevices);
        setMicrophones(micDevices);

        // Read the room's actual active device to sync the dropdown
        const activeCamTrack = room?.localParticipant
          ?.getTrackPublication(Track.Source.Camera)
          ?.track?.mediaStreamTrack;
        const activeMicTrack = room?.localParticipant
          ?.getTrackPublication(Track.Source.Microphone)
          ?.track?.mediaStreamTrack;

        const activeCamId = activeCamTrack?.getSettings()?.deviceId;
        const activeMicId = activeMicTrack?.getSettings()?.deviceId;

        if (!selectedCamera) {
          const initialCamera =
            activeCamId && cameraDevices.some((device) => device.deviceId === activeCamId)
              ? activeCamId
              : getPreferredDeviceId("camera", cameraDevices.map((d) => d.deviceId));
          setSelectedCamera(initialCamera);
        }
        if (!selectedMicrophone) {
          const initialMicrophone =
            activeMicId && micDevices.some((device) => device.deviceId === activeMicId)
              ? activeMicId
              : getPreferredDeviceId("microphone", micDevices.map((d) => d.deviceId));
          setSelectedMicrophone(initialMicrophone);
        }
      } catch (err) {
        console.error("Failed to enumerate devices:", err);
      }
    }

    getDevices();
  }, [room, selectedCamera, selectedMicrophone, t]);

  const handleCameraChange = async (deviceId: string) => {
    if (!room) return;
    setSelectedCamera(deviceId);
    saveDevicePreference("camera", deviceId);

    try {
      await room.switchActiveDevice("videoinput", deviceId);
    } catch (err) {
      console.error("Failed to switch camera:", err);
    }
  };

  const handleMicrophoneChange = async (deviceId: string) => {
    if (!room) return;
    setSelectedMicrophone(deviceId);
    saveDevicePreference("microphone", deviceId);

    try {
      await room.switchActiveDevice("audioinput", deviceId);
    } catch (err) {
      console.error("Failed to switch microphone:", err);
    }
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-none sm:w-[720px] lg:w-[840px] overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            {t("settings.title")}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 flex gap-6">
          {/* Left: Device controls */}
          <div className="flex-1 min-w-0 space-y-4">
            <div className="rounded-xl border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
              {t("settings.previewInSelfView")}
            </div>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="flex items-center gap-2 text-xs">
                  <Camera className="w-3.5 h-3.5" />
                  {t("settings.camera")}
                </Label>
                <select
                  className="w-full p-2 rounded-md border bg-background text-sm"
                  value={selectedCamera}
                  onChange={(e) => handleCameraChange(e.target.value)}
                >
                  {cameras.map((camera) => (
                    <option key={camera.deviceId} value={camera.deviceId}>
                      {camera.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <Label className="flex items-center gap-2 text-xs">
                  <Mic className="w-3.5 h-3.5" />
                  {t("settings.microphone")}
                </Label>
                <select
                  className="w-full p-2 rounded-md border bg-background text-sm"
                  value={selectedMicrophone}
                  onChange={(e) => handleMicrophoneChange(e.target.value)}
                >
                  {microphones.map((mic) => (
                    <option key={mic.deviceId} value={mic.deviceId}>
                      {mic.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Right: Background effects + extras */}
          <div className="w-[280px] flex-shrink-0 space-y-5">
            {/* Background Effects */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                {t("settings.background")}
              </Label>
              <BackgroundPicker
                mode={background.mode}
                blurRadius={background.blurRadius}
                selectedPreset={background.selectedPreset}
                presets={presets}
                isSupported={background.isSupported}
                isProcessing={background.isProcessing}
                onSelectNone={background.disable}
                onSelectBlur={background.setBlur}
                onSelectImage={background.setImage}
                onBlurRadiusChange={(radius: number) => background.setBlur(radius)}
              />
              {background.mode !== "none" && (
                <p className="text-xs text-muted-foreground">
                  {t("settings.previewInSelfView")}
                </p>
              )}
            </div>

            {/* Mirror Video */}
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <FlipHorizontal2 className="w-4 h-4" />
                {t("settings.mirrorVideo")}
              </Label>
              <Switch
                checked={isMirrored}
                onCheckedChange={onMirrorChange}
              />
            </div>

            {/* Tech Check */}
            <div className="pt-4 border-t">
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => setShowTechCheck(true)}
              >
                <MonitorCheck className="w-4 h-4" />
                {t("settings.techCheck")}
              </Button>
              <p className="text-xs text-muted-foreground mt-1.5">
                {t("settings.techCheckDescription")}
              </p>
            </div>

            {/* Connection Info */}
            {room && (
              <div className="pt-4 border-t">
                <Label className="text-muted-foreground text-xs">{t("settings.connectionInfo")}</Label>
                <div className="mt-2 text-xs space-y-1">
                  <p>{t("settings.server")}: {room.name || t("settings.connected")}</p>
                  <p>{t("settings.participants")}: {room.numParticipants}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tech Check Modal */}
        <TechCheckModal open={showTechCheck} onClose={() => setShowTechCheck(false)} />
      </SheetContent>
    </Sheet>
  );
}
